import axios, { GenericAbortSignal } from "axios";
import { BannerPlaceT, VehicleConditions, VehicleNames, VehicleTypesT } from "./GeneralTypes";
import axiosInstance from "./setupAxios";

export type CityResource = {
  id: number;
  name: string;
};

export type RegionResource = {
  id: number;
  name: string;
  city: CityResource;
  city_id: number;
};

export type ServiceResource = {
  id: number;
  title: string;
  icon: string;
  created_at: string | null;
  updated_at: string | null;
};

export type PaginationMetadata = {
  links: {
    first: string;
    last: string;
    next: string | null;
    prev: string | null;
  };
  meta: {
    current_page: number;
    from: number;
    last_page: number;
    path: string;
    per_page: number;
    to: number;
    total: number;
  };
};

// TODO: ADD TYPES TO ALL RESPONSES AND IT WILL PROPAGATE TO ALL COMPONENTS

export const getNewVehicles = async (vehicleType: VehicleTypesT, filter?: string) => {
  return await axiosInstance.get(`vehicles/${vehicleType}/for/new${filter ? filter : ""}`);
};

export const getNewVehiclesOffers = async (vehicleType: VehicleTypesT, filter?: string) => {
  return await axiosInstance.get(`offer/${vehicleType}${filter ? filter : ""}`);
};

export type getNewVehiclesOfSingleBrandRet = {
  models: {
    model_id: number;
    image: string;
    name: string;
    year: number;
    vehicles: {
      vehicle_id: number;
      price: number;
      class_name: string;
      image: string;
      selling_flag: boolean;
    }[];
  }[];
};
export const getNewVehiclesOfSingleBrand = async (vehicleType: VehicleTypesT, brandID: number) => {
  return await axiosInstance.get<getNewVehiclesOfSingleBrandRet>(`new/${vehicleType}/brand/${brandID}`);
};

export const getUsedVehicles = async (vehicleType: VehicleTypesT, filter?: string) => {
  return await axiosInstance.get(`vehicles/${vehicleType}/for/used${filter ? filter : ""}`);
};

export const getImportedVehicles = async (vehicleType: VehicleTypesT, filter?: string) => {
  return await axiosInstance.get(`vehicles/${vehicleType}/for/imported${filter ? filter : ""}`);
};

/**
 * getSingleVehicle user or showroom is selling(for distributor vehicles check `getDistributorVehicle`)
 */
export const getSingleVehicle = async (vehicleType: VehicleTypesT, vehicleCondition: VehicleConditions, id: number) => {
  const url = `vehicles/${vehicleCondition}/${vehicleType}/${id}`;
  return await axiosInstance.get(url);
};

/**
 * getDistributorVehicle is used to get a vehicle that is for sale by a distributor(not a showroom or user)
 */
export const getDistributorVehicle = async (vehicleType: VehicleTypesT, id: number) => {
  const url = `vehicles/class/${vehicleType}/${id}`;
  return await axiosInstance.get(url);
};

export type ShowroomBranch = {
  id: number;
  name: string;
  address: string;
  longitude: string | null;
  latitude: string | null;
  map_link: string;
  phone: string;
};

export type ShowroomResource = {
  id: number;
  name: string;
  info: string | null;
  phone: string;
  logo: string;
  cover: string | null;
  branches: ShowroomBranch[];
  website: string | null;
  yt: string | null;
  insta?: string | null;
  facebook: string | null;
};

export type ServiceCenterResource = {
  id: number;
  name: string;
  description: string;
  address: string;
  days_off: string;
  working_hours: string;
  phone: string;
  logo: string;
  cover: string;
  brands: BrandI[];
  website: string | null;
  yt: string | null;
  insta: string | null;
  facebook: string | null;
  services: ServiceResource[];
  region: RegionResource;
} & Record<VehicleTypesT, 0 | 1>;

export const getShowroomsFor = async (vehicleType: VehicleTypesT, filter?: string) => {
  return await axiosInstance.get<{
    showrooms: { data: ShowroomResource[] };
  }>(`showroom/showrooms/for/${vehicleType}${filter ? filter : ""}`);
};

export const getSingleShowroom = async (id: number) => {
  return await axiosInstance.get<{ showroom: ShowroomResource }>(`showroom/${id}`);
};

export const getSingleShowroomNewBrands = async (id: number, vehicleType: VehicleTypesT) => {
  return await axiosInstance.get(`showroom/brands/${id}/for/${vehicleType}`);
};

export const getSingleShowroomNewModels = async (id: number, brandID: number, vehicleType: VehicleTypesT) => {
  return await axiosInstance.get(`showroom/models/${id}/brand/${brandID}/for/${vehicleType}`);
};

// export const getSingleShowroomVehicles = async (
//   id: number,
//   vehicleCondition: VehicleConditions,
//   vehicleType: VehicleTypesT,
//   filter?: string,
// ) => {
//   return await axiosInstance.get(`showroom/${vehicleCondition}/${id}/for/${vehicleType}${filter ? filter : ""}`);
// };

export const getBrandsFor = async (vehicleCondition: VehicleConditions, vehicleType: VehicleTypesT) => {
  return await axiosInstance.get(`${vehicleCondition}/${vehicleType}/brands/brands`);
};

export const getUsedBrandsFor = async (vehicleType: VehicleTypesT) => {
  return await axiosInstance.get(`used-wizard/brands/for/${vehicleType}`);
};

export type BrandI = {
  id: number;
  /**
   * complete url
   */
  logo: string;
  name: string;
};
export const getSingleBrand = async (brandID: number) => {
  return await axiosInstance.get<{
    brand: BrandI;
  }>(`brand/${brandID}`);
};

export const getModelsFor = async (vehicleType: VehicleTypesT) => {
  return await axiosInstance.get(`${vehicleType}-model`);
};

export const getAttributesFor = async (vehicleType: VehicleTypesT) => {
  return await axiosInstance.get(`attribute/for/${vehicleType}`);
};

export const getAllVehicleCategories = async (
  vehicleType: VehicleTypesT,
  options?: { condition_code: VehicleConditions },
) => {
  return await axiosInstance.get(`${vehicleType}-category/all`, {
    params: options,
  });
};

export const getAllVehicleTypeCategoriesAsTree = async (vehicleType: VehicleTypesT) => {
  return await axiosInstance.get(`${vehicleType}-category/all/tree`);
};

export const getVehicleCategories = async (
  vehicleType: VehicleTypesT,
  level: number,
  vehicleCondition: VehicleConditions,
  id?: number,
) => {
  return await axiosInstance.get(`${vehicleType}-category/level${level}/for/${vehicleCondition}${id ? `/${id}` : ""}`);
};

export const getVehiclesOfSingleCategory = async (
  vehicleType: VehicleTypesT,
  vehicleConditions: VehicleConditions,
  catID: number,
  filter?: string,
) => {
  return await axiosInstance.get(
    `${vehicleType}-category/vehicles/for/${vehicleConditions}/${catID}${filter ? filter : ""}`,
  );
};

export const getCities = async () => {
  return await axiosInstance.get("city");
};

export const getRegions = async () => {
  return await axiosInstance.get("region");
};

type FindVehiclesResponse = {
  vehicles: {
    data: any[];
  } & PaginationMetadata;
};

/**
 * in search you could add `for_distributor=1` to make the search only for distributor vehicles, otherwise it will search for showroom and user vehicles
 * 
 * in search you can have field `sort` with one of the following options
      | "lowest_price" 
      | "highest_price"
      // sort by mileage
      | "lowest_kilometers"
      | "highest_kilometers"
      | "newest_year"
 */
export const findVehicles = async (
  state: VehicleConditions,
  vehicle: VehicleTypesT,
  search: string,
  pagination?: string,
  signal?: GenericAbortSignal,
) => {
  return await axiosInstance.get<FindVehiclesResponse>(
    `find/${state}/${vehicle}${search}${pagination ? `${search ? "&" : "?"}${pagination}` : ""}`,
    {
      signal,
    },
  );
};

/**
 * in search you could add `for_distributor=1` to make the search only for distributor vehicles, otherwise it will search for showroom and user vehicles
 *
 * in search you could add `only_count=1` and the API will return the count of the vehicles only
 *
 */
export const findVehiclesCount = async (state: VehicleConditions, vehicle: VehicleTypesT, search: string) => {
  return await axiosInstance.get<{ count: number }>(`find/${state}/${vehicle}${search}`);
};

export const getServiceCentersFor = async (vehicle: VehicleTypesT) => {
  return await axiosInstance.get<{
    service_centers: ServiceCenterResource[];
  }>("service-center");
};

export const getSingleServiceCenter = async (centerID: number) => {
  return await axiosInstance.get(`service-center/${centerID}`);
};

export const getComparedVehicles = async (
  vehicleType: VehicleTypesT,
  vehicleCondition: VehicleConditions,
  ids: string[],
) => {
  return await axiosInstance.get(`compare/${vehicleCondition}/${vehicleType}?ids=${ids.join(",")}`);
};

export const getArticles = async (filter?: string) => {
  return await axiosInstance.get(`article/all${filter ? filter : ""}`);
};

export const getReportReasons = async () => {
  return await axiosInstance.get("report/reasons");
};

export const getSingleArticle = async (id: string) => {
  return await axiosInstance.get(`article/${id}`);
};

export type RequestVehiclePayloadI = {
  /**
   * @default false
   */
  fleet?: boolean;
  phone?: string;
  name?: string;
  /**
   * Refers to an existing region ID
   */
  region_id: number;
  email?: string;
  cash: boolean;
  fleet_name?: string;
  position?: string;
  /**
   * Must be a number and at least 1
   */
  number_of_cars: number;
  /**
   * Nullable and string
   */
  comment?: string | null;
};

export const requestVehicle = async (vehicleType: VehicleTypesT, id: string, data: RequestVehiclePayloadI) => {
  return await axiosInstance.post(`request/${vehicleType}/${id}`, data);
};

// TODO: change type from any to specific type
export const reportVehicle = async (vehicleType: VehicleTypesT, id: string, data: any) => {
  return await axiosInstance.post(`report/vehicle/${vehicleType}/id/${id}`, data);
};

// TODO: change type from any to specific type
export const updateProfile = async (data: any) => {
  const updateFns = [axiosInstance.patch(`user/update`, { name: data.name })];
  if (data.profile_picture) {
    const form_data = new FormData();
    form_data.append("profile_picture", data.profile_picture);
    updateFns.push(axiosInstance.post(`user/upload/profile_picture`, form_data));
  }
  return await axios.all(updateFns);
};

// TODO: change type from any to specific type
export const sellVehicle = async (vehicleType: VehicleTypesT, data: any) => {
  return await axiosInstance.post(`used/${vehicleType}/create`, data);
};

// TODO: change type from any to specific type
export const updatedSoldVehicle = async (vehicleType: VehicleTypesT, id: string, data: any) => {
  return await axiosInstance.post(`used/${vehicleType}/update/${id}?_method=PATCH`, data);
};

// TODO: change type from any to specific type
export const uploadImagesToSellVehicle = async (vehicleType: VehicleTypesT, vehicleID: number, data: any) => {
  const form_data = new FormData();
  Array.from(data).forEach((file: any, index) => {
    form_data.append(`images[${index}]`, file);
  });
  return await axiosInstance.post(`used/${vehicleType}/upload/images/${vehicleID}`, form_data);
};

export const deleteImagesFromVehicle = async (vehicleType: VehicleTypesT, vehicleID: number, imageID: number) => {
  return await axiosInstance.delete(`used/${vehicleType}/${vehicleID}/images/${imageID}`);
};

export const assignAttributesToSellVehicle = async (vehicleType: VehicleTypesT, vehicleID: number, data: any) => {
  return await axiosInstance.post(`used/${vehicleType}/attribute/assign/${vehicleID}`, data);
};

// TODO: change type from any to specific type
export const updateAttributesToSellVehicle = async (
  vehicleType: VehicleTypesT,
  vehicleID: number,
  id: number,
  value: any,
) => {
  return await axiosInstance.patch(`used/${vehicleType}/${vehicleID}/attribute/update/${id}`, {
    value,
  });
};

// TODO: change type from any to specific type
export const removeAttributesFomSellVehicle = async (vehicleType: VehicleTypesT, vehicleID: number, id: number) => {
  return await axiosInstance.delete(`used/${vehicleType}/${vehicleID}/attribute/delete/${id}`);
};

export const getMyVehicles = async (vehicleType: VehicleTypesT, filter?: string) => {
  return await axiosInstance.get(`user/my-vehicles/${vehicleType}${filter ? filter : ""}`);
};

export const getBannersFiltered = async (place: BannerPlaceT, showroom_id?: string, service_center_id?: string) => {
  return await axiosInstance.get(
    `banner/filter?place=${place}${showroom_id ? `&showroom_id=${showroom_id}` : ""}${
      service_center_id ? `&service_center_id=${service_center_id}` : ""
    }`,
  );
};

export const requestCallback = async (vehicle_id: number, name: string, phone: string) => {
  return await axiosInstance.post("request-call/create", {
    vehicle_id,
    name,
    phone,
  });
};

export const deleteUserVehicle = async (vehicleType: VehicleTypesT, id: number) => {
  return await axiosInstance.delete(`used/${vehicleType}/delete/${id}`);
};

export const addPhoneWhatsappHit = (
  type: "phone" | "whatsapp",
  vehicleType: VehicleTypesT,
  vehicleCondition: VehicleConditions,
  id: string | number,
) => {
  return axiosInstance.post(
    type === "whatsapp"
      ? `/whatsapp/${vehicleCondition}/${vehicleType}/${id}`
      : `${vehicleCondition}/${vehicleType}/phone_hit/${id}`,
  );
};
